import styled, { css } from 'styled-components';

export const TalentMap = styled.div`
  --tm-navy: #032170;
  --tm-navy-hover: #021552;
  --tm-navy-deep: #0b1a4a;
  --tm-ink: #0b1430;
  --tm-text: #141a2b;
  --tm-text-2: #3b4357;
  --tm-muted: #5a6275;
  --tm-blue-grey: #bacdd1;
  --tm-page: var(--screening-canvas);
  --tm-surface: #ffffff;
  --tm-surface-2: #f7f8fb;
  --tm-chip: #f0f2f5;
  --tm-border: #e4e7ec;
  --tm-border-input: #dde2ea;
  --tm-divider: #eef0f4;
  --tm-navy-tint: #e6ebf6;
  --tm-nav-tint: #eef1f7;
  --tm-dash: #9aa4b8;
  --tm-wave: #c9d3e3;
  --tm-mark: #fcefc7;
  --tm-on-deep: #d5ddf0;
  --tm-strong-bg: #e3f1ea;
  --tm-strong-fg: #17623f;
  --tm-good-bg: #e6ebf6;
  --tm-good-fg: #032170;
  --tm-stretch-bg: #fbefd9;
  --tm-stretch-fg: #7a4b00;
  --tm-font-display: var(--screening-font-display);
  --tm-font-body: var(--screening-font-ui);
  --tm-r-page: 24px;
  --tm-r-card: 20px;
  --tm-r-block: 14px;
  --tm-r-control: 12px;
  --tm-search-shadow: 0 1px 2px rgba(11, 20, 48, 0.04),
    0 12px 32px rgba(11, 20, 48, 0.08);

  min-height: calc(100vh - 72px);
  background: var(--tm-page);
  color: var(--tm-text);
  font-family: var(--tm-font-body);
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;

  a {
    color: var(--tm-navy);
    text-decoration: none;
  }

  a:hover {
    color: var(--tm-navy-hover);
    text-decoration: underline;
  }

  button {
    font-family: inherit;
  }

  :focus-visible {
    outline: 2px solid var(--tm-navy);
    outline-offset: 2px;
  }

  mark {
    background: var(--tm-mark);
    color: inherit;
    border-radius: 3px;
    padding: 0 1px;
  }
`;

export const Column = styled.div<{ $narrow?: boolean; $gap?: number }>`
  box-sizing: border-box;
  width: 100%;
  max-width: ${({ $narrow }) => ($narrow ? '1040px' : '1120px')};
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 24}px;

  @media (max-width: 640px) {
    padding: 0 16px;
  }
`;

export const PageBody = styled(Column)`
  padding-top: 40px;
  padding-bottom: 96px;

  @media (max-width: 640px) {
    padding-top: 24px;
    padding-bottom: 64px;
  }
`;

export const Stack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 12}px;
  min-width: 0;
`;

export const Row = styled.div<{ $gap?: number; $between?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ $gap }) => $gap ?? 8}px;
  justify-content: ${({ $between }) =>
    $between ? 'space-between' : 'flex-start'};
  min-width: 0;
`;

export const Panel = styled.section`
  background: var(--tm-surface);
  border: 1px solid var(--tm-border);
  border-radius: var(--tm-r-page);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 20px;
    border-radius: var(--tm-r-card);
  }
`;

export const Card = styled.article<{ $selected?: boolean; $dashed?: boolean }>`
  background: var(--tm-surface);
  border: ${({ $selected, $dashed }) =>
    $selected
      ? '1px solid var(--tm-navy)'
      : $dashed
      ? '1px dashed #c9cfda'
      : '1px solid var(--tm-border)'};
  border-radius: var(--tm-r-card);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 18px;
  }
`;

export const Nested = styled.div`
  background: var(--tm-surface-2);
  border-radius: var(--tm-r-block);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const Title = styled.h1<{ $size?: number }>`
  margin: 0;
  font-family: var(--tm-font-display);
  font-weight: 600;
  font-size: ${({ $size }) => $size ?? 40}px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--tm-ink);

  @media (max-width: 640px) {
    font-size: ${({ $size }) => Math.min($size ?? 40, 30)}px;
  }
`;

export const SectionTitle = styled.h2<{ $size?: number }>`
  margin: 0;
  font-family: var(--tm-font-display);
  font-weight: 600;
  font-size: ${({ $size }) => $size ?? 22}px;
  line-height: 1.25;
  color: var(--tm-ink);
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: var(--tm-font-display);
  font-weight: 600;
  font-size: 20px;
  line-height: 1.25;
  color: var(--tm-ink);
`;

export const Body = styled.p<{ $size?: number; $tone?: 'text' | 'text-2' }>`
  margin: 0;
  font-size: ${({ $size }) => $size ?? 15}px;
  line-height: 1.55;
  color: ${({ $tone }) =>
    $tone === 'text' ? 'var(--tm-text)' : 'var(--tm-text-2)'};
`;

export const Meta = styled.span<{ $size?: number }>`
  font-size: ${({ $size }) => $size ?? 14}px;
  color: var(--tm-muted);
  min-width: 0;
`;

export const Eyebrow = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--tm-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const Pill = styled.span<{ $bg?: string; $fg?: string; $size?: number }>`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg ?? 'var(--tm-chip)'};
  color: ${({ $fg }) => $fg ?? 'var(--tm-text-2)'};
  font-size: ${({ $size }) => $size ?? 13}px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
`;

export const Tag = styled.span<{ $warn?: boolean }>`
  padding: 4px 9px;
  border-radius: 8px;
  background: ${({ $warn }) =>
    $warn ? 'var(--tm-stretch-bg)' : 'var(--tm-chip)'};
  color: ${({ $warn }) =>
    $warn ? 'var(--tm-stretch-fg)' : 'var(--tm-text-2)'};
  font-size: 12px;
  font-weight: 600;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  border-radius: var(--tm-r-control);
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  box-sizing: border-box;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:hover {
    text-decoration: none;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  border: 0;
  background: var(--tm-navy);

  &&,
  &&:hover {
    color: #ffffff;
  }

  &:hover:not(:disabled) {
    background: var(--tm-navy-hover);
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  border: 1px solid var(--tm-border-input);
  background: var(--tm-surface);
  color: var(--tm-navy);

  &:hover:not(:disabled) {
    border-color: var(--tm-dash);
    color: var(--tm-navy);
  }
`;

export const QuietButton = styled.button`
  ${buttonBase}
  border: 0;
  background: transparent;
  color: var(--tm-text-2);

  &:hover:not(:disabled) {
    color: var(--tm-ink);
  }
`;

export const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--tm-navy);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const TextInput = styled.input`
  flex: 1 1 200px;
  min-width: 0;
  min-height: 48px;
  box-sizing: border-box;
  border: 1px solid var(--tm-border-input);
  border-radius: var(--tm-r-control);
  padding: 0 14px;
  font-family: inherit;
  font-size: 15px;
  color: var(--tm-text);
  background: var(--tm-surface);

  &::placeholder {
    color: var(--tm-muted);
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const Columns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

export const MainColumn = styled.div`
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SideColumn = styled.div`
  flex: 1 1 340px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
